import { useState, useEffect, useContext, useCallback } from 'react';
import styled from 'styled-components';
import { Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import Table from '../custom/Table';
import Title from '../custom/Title';
import { FLOAT_REGEX, GOLD_VALUE, GRAIN_SKU, INVENTORY_TYPE, METAL_TYPES, NUMBER_REGEX, PATHS, SERIAL_NUMBER_REGEX, SILVER_VALUE, TRANSACTION_TYPES, USER_TYPES } from '../../utils/constant';
import { useNavigate, useParams } from 'react-router-dom';
import { TransactionContext } from '../../contexts/TransactionContext';
import { TColumn, TStepsProps, TTransactionTypes } from '../../Models/common';
import { NotificationContext } from '../../contexts/NotificationContext';
import useService from '../../hooks/useService';
import { completePhysicalOrder, createPhysicalOrder, getAllProducts, getLotDetails, getPhysicalOrder, getProducts as getProductsService } from '../../services/Transactions';
import { TCartItem, TLineItem, TProductsColumns, TSku, TSkuAmounts } from '../../Models/PhysicalOrder';
import { AddShoppingCart, RemoveShoppingCart } from '@mui/icons-material';
import CustomTabs from '../custom/newTabs';
import DynamicForm, { TField, useDynamicForm } from '../forms/DynamicFormNew';
import { getCustomerActiveAddresses } from '../../services/Customer';
import { CustomerContext } from '../../contexts/CustomerContext';
import AddressCard from '../custom/AddressCard';
import { TOrder } from '../../Models/Transactions';
import { TCompletePhysicalOrderParams } from '../../services/Transactions/types';
import StepNavigation from '../custom/StepNavigation';
import { TCustomerFields } from '../../Models/Inbound';
import { ICustomerAddress, ICustomerBasic } from '../../Models/Customer';
import { log } from 'console';
import { getAddressesOptions, getFormattedCurrency, getThreePointNumber } from '../../utils/helpers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Modal from '../Modals';
import SerialNumberSelectorModal from './SerialNumberSelector';
import { AuthContext } from '../../contexts/AuthContext';
import { getPvcCenterAddresses } from '../../services/PVC';
import { TPvcCenterAddress } from '../../Models/PVC';


const Divider = styled.div`
    border: 1px solid #EBEBEC;
`
type TOrderDetails = {
    status: string;
    orderId: number;
    orderDate: string;
    customerId: string;
    billingAddressId: string;
    shippingAddressId: string;
}
export const ORDER_DETAILS_FORM: TField<TCompletePhysicalOrderParams>[] = [
    {
        fieldName: 'id',
        isVisible: true,
        fieldType: 'text',
        label: 'Order #',
        disabled: true,
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'status',
        isVisible: true,
        fieldType: 'text',
        label: 'Status',
        disabled: true,
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'billingAddressId',
        isVisible: true,
        fieldType: 'select',
        label: 'Billing Address',
        width: 'calc(33% - 20px)',
        options: [],
        validation: {
            required: true
        }
    },
    {
        fieldName: 'shippingAddressId',
        isVisible: true,
        fieldType: 'select',
        label: 'Shipping Address',
        width: 'calc(33% - 20px)',
        options: [],
        validation: {
            required: true
        }
    },
    {
        fieldName: 'isShippingAsbillingAddressId',
        isVisible: false,
        fieldType: 'checkbox',
        label: 'is Shipping same As Billing Address',
        width: 'calc(33% - 20px)',
    },
    {
        fieldName: 'isDoorstepDelivery',
        isVisible: true,
        fieldType: 'checkbox',
        label: 'Deliver to Door Steps?',
        width: 'calc(33% - 20px)',
    },
]

export const LINE_ITEM_COLUMNS: TColumn<TLineItem & { totalWeight: number }>[] = [
    {
        field: 'productId',
        headerName: 'Item #',
        minWidth: 180
    },
    {
        field: 'productName',
        headerName: 'Name',
        minWidth: 800,
        renderCell(params) {
            const value = params.row.product?.product_name
            return <Tooltip title={value}>
                <div>{value}</div>
            </Tooltip>
        },
    },
    {
        field: 'metalType',
        headerName: 'Metal Type',
        minWidth: 180
    },
    {
        field: 'quantity',
        headerName: 'Quantity',
        minWidth: 180
    },
    {
        field: 'weight',
        headerName: 'Unit Weight',
        minWidth: 180
    },
    {
        field: 'totalWeight',
        headerName: 'Total Weight',
        renderCell(params) {
            return getThreePointNumber(params.row.weight * params.row.quantity)
        },
    },
]

export const MINTED_LINE_ITEM_COLUMNS: TColumn<TLineItem>[] = [
    {
        field: 'productId',
        headerName: 'Item #',
        minWidth: 180
    },
    {
        field: 'quantity',
        headerName: 'Quantity'
    },
    {
        field: 'unitPrice',
        headerName: 'Unit Price (Excl. Tax)(Rs)',
        minWidth: 200
    },
    {
        field: 'unitPriceTaxInclusive',
        headerName: 'Metal Price(Rs)',
        minWidth: 120
    },
    {
        field: 'totalValue',
        headerName: 'Total MRP(Rs)',
        minWidth: 120
    },
    {
        field: 'totalTax',
        headerName: 'Total Tax(Rs)',
        minWidth: 120
    },
]

export const getAmount = (skuAmount: TSkuAmounts, quantity: any, customer: ICustomerBasic) => {
    let amount = 0
    if (customer.userType === USER_TYPES.FNF) {
        console.log('getAmount for fnf')
        amount = getTwoPointNumber(skuAmount?.FNF * quantity)
    }
    else if (customer.userType === USER_TYPES.EMPLOYEE) {
        console.log('getAmount for employee')
        amount = getTwoPointNumber(skuAmount?.EMPLOYEE * quantity)
    }
    else if (customer.isCorporate === 'Y') {
        console.log('getAmount for b2b')
        amount = getTwoPointNumber(skuAmount?.B2B * quantity)
    }
    else {
        console.log('getAmount for retail')
        amount = getTwoPointNumber(skuAmount?.RETAIL * quantity)
    }
    return amount
}

const getTwoPointNumber = (number: number) => {
    return Number(number.toFixed(2))
}

export const getTotalValue = (product: TSku, quantity: any, cutomer: ICustomerBasic) => {
    return Number((quantity * getAmount(product.postTaxAmount, 1, cutomer)).toFixed(2))
}

export const getMetalType = (product: TSku) => {
    const metaltypeItem = product.categories.filter(categoryItem => categoryItem.type === 'Metal Type')[0]
    return metaltypeItem ? metaltypeItem.values[0] : ''
}

const PhysicalOrder = (props: TStepsProps) => {
    const { path, nextButtonLable, nextSection, prevSection } = props
    const navigate = useNavigate()
    const { capturedSteps, transactionInfo, physicalOrder } = useContext(TransactionContext)
    const { notify } = useContext(NotificationContext)
    const { execute } = useService()
    const [showOtherDetails, setShowOtherDetails] = useState(false)
    const { id } = useParams()

    const redirectToIncompleteStep = () => {
        if (prevSection && !capturedSteps.includes(prevSection)) {
            goToPrevSection()
        }
    }

    const goToNextSection = () => {
        if (!capturedSteps.includes('PHYSICAL_ORDER'))
            return notify('Complete Physical Order first.', 'error')

        if (id) navigate(path + '/' + id + '?section=' + nextSection)
        else navigate(path + '?section=' + nextSection)
    }

    const goToPrevSection = () => {
        if (id) navigate(path + '/' + id + '?section=' + prevSection)
        else navigate(path + '?section=' + prevSection)
    }


    useEffect(() => {
        redirectToIncompleteStep()
        console.log('physicalOrder', physicalOrder)
        if (physicalOrder?.id) {
            execute(() => getPhysicalOrder({
                globalTransactionNumber: transactionInfo.globalTransactionNumber!,
                transactionType: transactionInfo.transactionType
            }))
            setShowOtherDetails(true)
        }
    }, [])



    return (
        <div style={{ padding: '0 10px' }}>
            {
                showOtherDetails ?
                    <PhysicalOrderDetails goToNextStep={goToNextSection} /> :
                    <Products closeProducts={() => setShowOtherDetails(true)} />
            }
            <StepNavigation
                capturedSteps={capturedSteps}
                currentSection='PHYSICAL_ORDER'
                goToNextSection={goToNextSection}
                nextButtonLable={nextButtonLable}
                goToPrevSection={goToPrevSection}
                prevSection={prevSection}
            />
        </div>
    )
}

const Products = ({ closeProducts }: { closeProducts: () => any }) => {
    const navigate = useNavigate()
    const { products, cart, setCart, transactionInfo, inboundOrder, inboundDetails, meltingLot } = useContext(TransactionContext)
    const { notify } = useContext(NotificationContext)
    const { customerConfig } = useContext(CustomerContext)
    const { execute } = useService()
    const [quantity, setQuantity] = useState<{ [key: string]: string }>({})
    // const [serialNumbers, setSerialNumbers] = useState<{ [key: string]: string }>({})
    const [productRows, setProductRows] = useState<TProductsColumns[]>([])
    const { transactionType } = transactionInfo
    const [showSerialNumberModal, setShowSerialNumberModal] = useState<boolean | string>(false);
    const [availableSerialNumbers, setAvailableSerialNumbers] = useState<{ [key: string]: number[] }>({})
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<{ [key: string]: number[] }>({})
    const [loading, setLoading] = useState(false)


    const productColumns: TColumn<TProductsColumns>[] = [
        {
            field: 'skuId',
            headerName: 'Item Number',
            minWidth: 250
        },
        {
            field: 'name',
            headerName: 'Name',
            minWidth: 350
        },
        {
            field: 'description',
            headerName: 'Item Description',
            minWidth: 250,
            renderCell(params) {
                let div = document.createElement("div");
                div.innerHTML = params.row.description;
                let text = div.textContent || div.innerText || "";
                return text
            },
        },
        {
            field: 'purity',
            headerName: 'Purity',
        },
        {
            field: 'weight',
            headerName: 'Unit Weight',
        },
        {
            field: 'totalWeight',
            headerName: 'Net Weight',
            renderCell(params) {
                const itemInCart = cart.filter(product => params.id === product.productId)[0]
                return itemInCart ? getTwoPointNumber(params.row.weight * itemInCart.quantity) : 0
            },
        },
        (transactionType === TRANSACTION_TYPES.MINTED_SALES ? {
            field: 'amount',
            headerName: 'Amount (Incl. Taxes)',
            minWidth: 130,
            renderCell(params) {
                const itemInCart = cart.filter(product => params.id === product.productId)[0]
                return itemInCart ? getTwoPointNumber(itemInCart.totalValue) : 0
            },
        } : {
            field: 'makingChargesAfterDiscount',
            headerName: 'Making Charges',
            minWidth: 130,
            renderCell(params) {
                return getFormattedCurrency(getAmount(params.row.makingChargesAfterDiscount, 1, customerConfig))
            },
        }),
        {
            field: 'stock',
            headerName: 'Available Stock',
            minWidth: 130,
            renderCell(params) {
                const { current_weight, inventory_type, quantity } = params.row.inventory
                return inventory_type === INVENTORY_TYPE.CUT_BAR_LOOSE_ITEMS ? Number(current_weight.toFixed(3)) : quantity
            },
        },
        {
            field: 'serialNumbers',
            headerName: 'Serial Numbers',
            minWidth: 60,
            renderCell(params) {

                return params.row.isSerialNo && transactionType !== TRANSACTION_TYPES.JOB_WORK ?
                    <>
                        {quantity[params.row.skuId] !== '0' && <IconButton onClick={() => openSerialModal(params.row.skuId)}>
                            <VisibilityIcon />
                        </IconButton>}
                    </>
                    : <></>
            },
        },
        {
            field: 'quantity',
            headerName: 'Quantity',
            minWidth: 150,
            renderCell(params) {
                return <TextField
                    disabled={!cart.filter(product => params.id === product.productId)[0]}
                    value={quantity[params.id]}
                    onChange={e => changeQuantity(e, params.id as string)}
                    size='small'
                    onKeyDown={e => e.stopPropagation()}
                />
            },
        },
        {
            field: 'actions',
            headerName: '',
            renderCell(params) {
                return cart.filter(product => params.id === product.productId)[0] ?
                    <Tooltip title='Remove From Cart'>
                        <IconButton onClick={() => removeFromCart(params.id as string)}>
                            <RemoveShoppingCart />
                        </IconButton>
                    </Tooltip> :
                    <Tooltip title='Add To Cart'>
                        <IconButton onClick={() => addToCart(params.id as string)}>
                            <AddShoppingCart />
                        </IconButton>
                    </Tooltip>
            },
        },
    ]

    const productToRows = (products: TSku[]) => {
        const items: TProductsColumns[] = []
        for (let sku of products) {
            let pushThisItem = true
            // removing silver from single metal transactions
            if ([TRANSACTION_TYPES.BUY_SELL, TRANSACTION_TYPES.JOB_WORK].includes(transactionType)) {
                if (getMetalType(sku) === METAL_TYPES.SILVER) pushThisItem = false
            }

            if (pushThisItem) {
                items.push({
                    ...sku,
                    totalWeight: 0,
                } as TProductsColumns)
            }
        }

        return items
    }

    const getProductRows = () => {
        return productToRows(products)
            // .filter(product => {
            //     if (TRANSACTION_TYPES.MINTED_SALES) {
            //         return product
            //     } else {
            //         return product.purity == inboundDetails.conversionFineness
            //     }
            // })
            .sort((a, b) => {
                const cartIds = cart.map(item => item.productId);
                (a as any).inCart = cartIds.includes(a.skuId);
                (b as any).inCart = cartIds.includes(b.skuId);
                return (b as any).inCart - (a as any).inCart
            })
    }

    const changeQuantity = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, id: string) => {
        const value = e.target.value
        const product = productRows.filter(product => id === product.skuId)[0]
        console.log('is cutbar', product.inventory.inventory_type)
        if (!value.length) return setQuantity(prev => ({ ...prev, [id]: '0' }))
        else if (product.inventory.inventory_type === INVENTORY_TYPE.CUT_BAR_LOOSE_ITEMS && FLOAT_REGEX.test(value)) setQuantity(prev => ({ ...prev, [id]: value }))
        else if (NUMBER_REGEX.test(value)) {
            setSelectedSerialNumbers(prev => ({ ...prev, [id]: [] }))
            setQuantity(prev => ({ ...prev, [id]: value }))
        }

        quantityHandler(id, Number(value), product)
    }

    const openSerialModal = (id: string) => {
        setShowSerialNumberModal(id);
    }


    const initializeStates = () => {
        const rows = getProductRows()
        setQuantity(
            rows
                .reduce((productsObj, product) => {
                    return { ...productsObj, [product.skuId]: '0' }
                }, {})
        )
        setSelectedSerialNumbers(
            rows
                .reduce((productsObj, product) => {
                    if (product.isSerialNo) {
                        return { ...productsObj, [product.skuId]: [] }
                    } else {
                        return productsObj
                    }
                }, {})
        )
    }

    const addToCart = (id: string) => {
        console.log('productRows...', productRows)
        const product = productRows.filter(product => id === product.skuId)[0]
        setQuantity(prev => ({ ...prev, [id]: '1' }))
        console.log('product...', product)

        setCart(prev => ([
            ...prev,
            {
                productId: product.skuId,
                discount: 0,
                isDigital: true,
                metalType: getMetalType(product),
                quantity: 1,
                serialNumbers: '',
                taxPercentage: product.taxPercentage,
                totalTax: getAmount(product.taxAmount, 1, customerConfig),
                totalValue: getAmount(product.postTaxAmount, 1, customerConfig),
                unitPrice: getAmount(product.preTaxAmount, 1, customerConfig),
                unitPriceTaxInclusive: getAmount(product.postTaxAmount, 1, customerConfig),
                weight: product.fineWeight,
                isSerialNo: product.isSerialNo,
                productName: product.name
            }]
        ))
    }

    const quantityHandler = (id: string, quantity: number, product: TProductsColumns) => {
        if (!quantity) {
            removeFromCart(id)
        }
        else {
            const totalTax = getAmount(product.taxAmount, quantity, customerConfig)
            const totalValue = getTotalValue(product, quantity, customerConfig)
            setCart(prev => ([
                ...prev.map(cartItem => {
                    return id === cartItem.productId ? {
                        ...cartItem,
                        quantity,
                        totalTax,
                        totalValue
                    } : cartItem
                })
            ]
            ))
        }
    }

    const removeFromCart = (id: string) => {
        setQuantity(prev => ({ ...prev, [id]: '0' }))
        setSelectedSerialNumbers(prev => ({ ...prev, [id]: [] }));
        setCart(prev => ([...prev.filter(cartItem => id != cartItem.productId)]))
    }

    const getProducts = async () => {
        const { data } = transactionInfo.transactionType === TRANSACTION_TYPES.JOB_WORK ? await execute(() => getAllProducts({})) : await execute(() => getProductsService({}))
        console.log('products data', data)
    }
    const getMeltingLots = async () => {
        const { data } = await execute(() => getLotDetails({
            globalTransactionNumber:transactionInfo.globalTransactionNumber!
        }))
        console.log('lots data', data)
    }

    const getSellOrderValue = () => {
        if(transactionType === TRANSACTION_TYPES.JOB_WORK) {
            return meltingLot.reduce((totalMetalFineWeightToReturn, lot) =>{
                return totalMetalFineWeightToReturn + Number(lot.fineWeightToReturn)
            },0)
        }
        return Number(inboundOrder.totalGoldWeight) + Number(inboundOrder.totalSilverWeight)
    }

    const getCutbarProduct = useCallback(() => {
        return productRows.filter(product => product.inventory.inventory_type === INVENTORY_TYPE.CUT_BAR_LOOSE_ITEMS)[0]
    }, [productRows])

    const submitHandler = async () => {
        setLoading(true)
        switch (transactionType) {
            case TRANSACTION_TYPES.BUY_SELL:
                submitBuysell()
                break;
            case TRANSACTION_TYPES.JOB_WORK:
                submitJobwork()
                break;
            case TRANSACTION_TYPES.MINTED_SALES:
                submitMintedSales()
                break;
        }
        setLoading(false)
    }

    const submitBuysell = async () => {
        const sellOrderValue = getThreePointNumber(getSellOrderValue())
        console.log('products', productRows)
        const cutBarProduct = getCutbarProduct()

        let cartValue = 0
        let cutBarValue = 0
        let cutBarCartItem
        const cartItemsWithoutSerialNumbers = []
        console.log('cutBarProduct', cutBarProduct)
        for (let cartItem of cart) {
            console.log('cartItem', cartItem)
            cartValue += getThreePointNumber(cartItem.weight * cartItem.quantity)
            console.log('cutbar condition :', cutBarProduct && cartItem?.productId === cutBarProduct?.skuId)
            console.log('cutbar condition :', cartItem?.productId, cutBarProduct?.skuId)
            if (cutBarProduct && cartItem?.productId === cutBarProduct?.skuId) {
                cutBarValue = cartItem.quantity * cartItem.weight
                cutBarCartItem = cartItem
            } else {
                if (!cartItem.serialNumbers && cartItem.isSerialNo) cartItemsWithoutSerialNumbers.push(cartItem)
            }
        }

        cartValue = getThreePointNumber(cartValue)
        cutBarValue = getThreePointNumber(cutBarValue)

        if (cartItemsWithoutSerialNumbers.length > 0) return notify(`Enter serial numbers for item ${cartItemsWithoutSerialNumbers[0].productName}`, 'error')

        let nonCutBarValue = getThreePointNumber(cartValue - cutBarValue)
        let requiredCutBarQuantity = 0

        console.log('cutBarCartItem', cutBarCartItem)
        console.log('totalValue', cartValue)
        console.log('inboundOrderTotalPrice', sellOrderValue)
        console.log('nonCutBarValue', nonCutBarValue)
        console.log('cutbar prity', cutBarProduct?.purity)
        console.log('cutBarCartItem?.unitPriceTaxInclusive!', cutBarCartItem?.unitPriceTaxInclusive!)

        if (cartValue - 0.000998 > sellOrderValue) {
            if (nonCutBarValue > sellOrderValue) return notify('Products total value is greater than the value of given metal', 'error')
            else {
                requiredCutBarQuantity = getThreePointNumber((sellOrderValue - nonCutBarValue) / (+cutBarProduct.purity / 1000))
                return notify(`You can add ${requiredCutBarQuantity} quantity of Cut Bar...`, 'error')
            }
        }
        else if (cartValue < sellOrderValue) {
            if (cutBarValue > 0) {
                requiredCutBarQuantity = getThreePointNumber((sellOrderValue - nonCutBarValue) / (+cutBarProduct.purity / 1000))
                return notify(`You can add ${requiredCutBarQuantity} quantity of Cut Bar`, 'error')
            }
            else {
                return notify(`Products total value is less than the value of given metal.`, 'error')
            }
        }

        const { data, error } = await execute(() => createPhysicalOrder({
            globalTransactionNumber: transactionInfo.globalTransactionNumber!,
            lineItems: cart,
            transactionType: transactionInfo.transactionType
        }))

        if (error === null && data != null) {
            notify('Order Created.')
            closeProducts()
        }
    }

    const submitJobwork = async () => {
        const sellOrderValue = getThreePointNumber(getSellOrderValue())

        console.log('products...', products)
        const grainProduct = products.filter(product => product.skuId === GRAIN_SKU)[0]

        let cartValue = 0
        let grainValue = 0
        let grainCartItem: TLineItem | undefined

        for (let cartItem of cart) {
            console.log('cartItem', cartItem)
            cartValue += getThreePointNumber(cartItem.weight * cartItem.quantity)
            if (grainProduct && cartItem?.productId === grainProduct.skuId) {
                console.log('grainProduct', grainProduct)
                console.log('cartItem', cartItem)
                grainValue = getThreePointNumber(cartItem.quantity / 1000)
                grainCartItem = cartItem
            }
        }

        cartValue = getThreePointNumber(cartValue)
        grainValue = getThreePointNumber(grainValue)

        let nonGrainValue = getThreePointNumber(cartValue - grainValue)
        let requiredGrainQty = 0

        console.log('totalValue', cartValue)
        console.log('inboundOrderTotalPrice', sellOrderValue)
        console.log('nonGrainValue', nonGrainValue)
        console.log('grainValue', grainValue)
        console.log('grainCartItem?.unitPriceTaxInclusive!', grainCartItem?.unitPriceTaxInclusive!)

        if (cartValue < sellOrderValue) {
            if (grainValue > 0) {
                requiredGrainQty = Math.floor((sellOrderValue - nonGrainValue) * 1000)
                return notify(`You can add ${requiredGrainQty} quantity of Grain`, 'error')
            }
            else {
                return notify(`Products total value is less than the value of given metal.`, 'error')
            }
        }
        else if (cartValue > sellOrderValue) {
            if (nonGrainValue > sellOrderValue) return notify('Products total value is greater than the value of given metal', 'error')
            else {
                requiredGrainQty = getThreePointNumber((sellOrderValue - nonGrainValue) * 1000)
                return notify(`You can add ${requiredGrainQty} quantity of Cut Bar...`, 'error')
            }
        }


        const { data, error } = await execute(() => createPhysicalOrder({
            globalTransactionNumber: transactionInfo.globalTransactionNumber!,
            // lineItems: discount ? cart.map(item => item.productId === GRAIN_SKU ? { ...item, discount } : item) : cart,
            lineItems: cart,
            transactionType: transactionInfo.transactionType
        }))

        if (error === null && data != null) {
            notify('Order Created.')
            closeProducts()
        }
    }

    const submitMintedSales = async () => {

        for (let lineItem of cart) {
            if (lineItem.isSerialNo && selectedSerialNumbers[lineItem.productId]) {
                const serialNumbersOfSku = selectedSerialNumbers[lineItem.productId]

                if (lineItem.isSerialNo && serialNumbersOfSku.length > lineItem.quantity) {
                    return notify(`Serial Numbers can't be more than quantity for item ${lineItem.productName}`, 'error')
                } else if (lineItem.isSerialNo && serialNumbersOfSku.length < lineItem.quantity) {
                    return notify(`Serial Numbers can't be less than quantity for item ${lineItem.productName}`, 'error')
                }
            }
        }

        const cartItemsWithoutSerialNumbers = []
        console.log("card", cart);
        if (!cart.length) {
            return notify('Add Products into cart', 'error')
        }
        for (let cartItem of cart) {
            console.log('cartItem', cartItem)
            if (!cartItem.serialNumbers && cartItem.isSerialNo) cartItemsWithoutSerialNumbers.push(cartItem)
        }

        if (cartItemsWithoutSerialNumbers.length > 0) return notify(`Enter serial numbers for item ${cartItemsWithoutSerialNumbers[0].productId}`, 'error')

        const { data, error } = await execute(() => createPhysicalOrder({
            globalTransactionNumber: transactionInfo.globalTransactionNumber!,
            lineItems: cart,
            transactionType: transactionInfo.transactionType
        }))

        if (error === null && data != null) {
            notify('Order Created.')
            closeProducts()
        }
    }

    const getRemainingAmount = useCallback(() => {
        let cartValue = 0
        // const cutbarProduct = getCutbarProduct()

        for (let cartItem of cart) {
            // if (cutbarProduct && cartItem.productId === cutbarProduct.skuId) {
            //     cartValue += cartItem.quantity
            // } else {
            cartValue += getThreePointNumber(cartItem.weight * cartItem.quantity)
            // }
        }
        cartValue = getThreePointNumber(cartValue)
        console.log('cart', cart)
        console.log('cartValue', cartValue)
        return getThreePointNumber(getSellOrderValue() - cartValue)
    }, [cart, meltingLot])

    useEffect(() => {
        getProducts()
    }, [])

    useEffect(() => {
        if(transactionInfo.transactionType === TRANSACTION_TYPES.JOB_WORK) getMeltingLots()
    }, [transactionInfo])

    useEffect(() => {
        products.map((product) => {
            if (product.isSerialNo) {
                const parsedSerialNumbers = JSON.parse(product.inventory.serial_numbers);
                setAvailableSerialNumbers((prev) => ({ ...prev, [product.skuId]: parsedSerialNumbers }))
            }
        })
        setProductRows(getProductRows())
    }, [products])

    useEffect(() => {
        if (productRows.length) initializeStates()
    }, [productRows])

    console.log('cart', cart)

    return (
        <div style={{ padding: '10px 0' }}>
            <SerialNumberSelectorModal
                availableSerialNumbers={availableSerialNumbers[showSerialNumberModal as string]}
                onClose={(updatedSerialNumbers) => {
                    setSelectedSerialNumbers(prev => ({ ...prev, [showSerialNumberModal as string]: updatedSerialNumbers }))
                    setCart(prev => {
                        return [...prev.map(item => {
                            if (item.productId === showSerialNumberModal) {
                                return { ...item, serialNumbers: updatedSerialNumbers.join(',') }
                            } else return item
                        })]
                    })
                    setShowSerialNumberModal(false)
                }}
                quantity={+quantity[showSerialNumberModal as string]}
                selectedSerialNumbers={selectedSerialNumbers[showSerialNumberModal as string]}
                show={Boolean(showSerialNumberModal)}
            />
            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                <Table columns={productColumns} rows={getProductRows()} getRowId={(row: TProductsColumns) => row.skuId} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <Button variant='contained' onClick={submitHandler} disabled={loading} >Create Order</Button>
                    {transactionType !== TRANSACTION_TYPES.MINTED_SALES && <Chip label={<>Metal Left: <span style={{ color: getRemainingAmount() < 0 ? 'red' : 'inherit' }}>{getRemainingAmount()} gm</span></>} />}
                </div>
            </div>
        </div>
    )
}


export const PhysicalOrderDetails = ({ goToNextStep }: { goToNextStep: () => any }) => {
    const { registerForm, setFormConfig, setFormData, formData, setField } = useDynamicForm({ formConfig: ORDER_DETAILS_FORM })
    const { customerConfig, customerAddresses } = useContext(CustomerContext)
    const { lineItems, physicalOrder, transactionInfo, setCapturedSteps } = useContext(TransactionContext)
    const { notify } = useContext(NotificationContext)
    const { user: { centerId }, pvcAddresses } = useContext(AuthContext)
    const { execute } = useService()
    const navigate = useNavigate()
    const [forceNavigate, setForceNavigate] = useState(false)


    const submitHandler = async (formData: TCompletePhysicalOrderParams) => {
        console.log('physical order', formData)
        const { error } = await execute(() => completePhysicalOrder({
            ...physicalOrder,
            ...formData,
            transactionType: transactionInfo.transactionType,
            globalTransactionNumber: transactionInfo.globalTransactionNumber!
        }))

        if (error === null) {
            notify('Order placed successfully')
            setCapturedSteps(prev => ([...prev, 'PHYSICAL_ORDER']))
            setForceNavigate(true)
        }
    }

    const updateForm = () => {
        setField('customerId', { options: [{ label: customerConfig.displayName, value: customerConfig.customerRefNo }] })
    }

    useEffect(() => {
        if (customerAddresses.length) {
            setField('billingAddressId', { options: getAddressesOptions(customerAddresses), disabled: Boolean(physicalOrder.totalPrice) })
            setField('shippingAddressId', { options: getAddressesOptions(customerAddresses), disabled: Boolean(physicalOrder.totalPrice) })
        }
    }, [customerAddresses, pvcAddresses])

    useEffect(() => {
        updateForm()
        if (customerConfig.customerRefNo) execute(() => getCustomerActiveAddresses({ customerRefNo: customerConfig.customerRefNo! }))
    }, [centerId, customerConfig])

    useEffect(() => {
        const isShippingAsbillingAddressId = formData.shippingAddressId === formData.billingAddressId
        setFormData({ ...formData, isShippingAsbillingAddressId })
    }, [formData.shippingAddressId, formData.billingAddressId])

    useEffect(() => {
        const shippingAddressId = formData.isShippingAsbillingAddressId && formData.billingAddressId ? formData.billingAddressId : ''
        setFormData({ ...formData, shippingAddressId: shippingAddressId })
    }, [formData.isShippingAsbillingAddressId])

    useEffect(() => {
        console.log('customerConfig.isCorporate', customerConfig.isCorporate)

        setField('shippingAddressId', { isVisible: true, options: getAddressesOptions(customerAddresses) })
        setField('billingAddressId', { isVisible: true, options: getAddressesOptions(customerAddresses) })


        setFormData({
            id: physicalOrder.id,
            status: physicalOrder.status,
            billingAddressId: physicalOrder.billingAddressId,
            shippingAddressId: physicalOrder.shippingAddressId,
            isDoorstepDelivery: physicalOrder.isDoorstepDelivery,
            isShippingAsbillingAddressId: physicalOrder.billingAddressId === physicalOrder.shippingAddressId
        } as TCompletePhysicalOrderParams)

    }, [physicalOrder])

    useEffect(() => {
        if (forceNavigate) goToNextStep()
    }, [forceNavigate])

    useEffect(() => {
        console.log('lineItems...', lineItems)
        lineItems.map((el: TLineItem) => {
            if (el.totalValue) {
                el.totalValue = getFormattedCurrency(el.totalValue, true)
            }
            if (el.unitPrice) {
                el.unitPrice = getFormattedCurrency(el.unitPrice, true)
            }
            if (el.totalTax) {
                el.totalTax = getFormattedCurrency(el.totalTax, true)
            }
            if (el.unitPriceTaxInclusive) {
                el.unitPriceTaxInclusive = getFormattedCurrency(el.unitPriceTaxInclusive, true)
            }
        })
    }, [lineItems])

    useEffect(() => {
        setField('isDoorstepDelivery', { isVisible: transactionInfo.transactionType != TRANSACTION_TYPES.BUY_SELL })
    }, [])

    return <>
        <div style={{ padding: '10px 0' }}>
            <Title>Physical Order Details</Title>
            <Divider style={{ marginBottom: '10px' }} />
            <DynamicForm {...registerForm()} onSubmit={physicalOrder.totalPrice ? undefined : submitHandler} submitText='Confirm' />
        </div>
        <div style={{ display: 'flex', gap: '20px' }}>
            <div style={{ width: 'calc(50% - 10px)' }}>
                {formData.billingAddressId && customerAddresses.length ? <AddressCard address={customerAddresses.filter(address => address.id === formData.billingAddressId)[0]} heading='Billing Address' /> : <></>}
            </div>
            <div style={{ width: 'calc(50% - 10px)' }}>
                {formData.isDoorstepDelivery || customerConfig.isCorporate === 'Y' ?
                    (formData.shippingAddressId && customerAddresses.length > 0 ? <AddressCard address={customerAddresses.filter(address => address.id === formData.shippingAddressId)[0]} heading='Shipping Address' /> : <></>)
                    :
                    (formData.shippingAddressId && pvcAddresses.length > 0 ? <AddressCard address={pvcAddresses.filter(address => address.id === formData.shippingAddressId)[0]} heading='Shipping Address' /> : <></>)
                }
            </div>
        </div>
        <div style={{ padding: '20px 0' }}>
            <Title>Line Items</Title>
            <Divider style={{ marginBottom: '10px' }} />
            <Table columns={transactionInfo.transactionType === TRANSACTION_TYPES.MINTED_SALES ? MINTED_LINE_ITEM_COLUMNS : LINE_ITEM_COLUMNS} rows={lineItems} />
        </div>
    </>
}

export default PhysicalOrder
